import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ui/ThemeToggle';

// Only allow redirects to internal portal paths
function safeNext(raw) {
  if (!raw) return '/portal';
  return raw.startsWith('/portal') ? raw : '/portal';
}

export default function Login() {
  const theme    = useTheme();
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  // Support ?next=/portal/upgrade from native app deep links
  const params = new URLSearchParams(location.search);
  const from   = safeNext(location.state?.from || params.get('next'));

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [loaded,   setLoaded]   = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.success) navigate(from, { replace: true });
    else setError(res.error || 'Login failed');
  }

  const inputStyle = {
    backgroundColor: theme.background,
    borderColor:     theme.border,
    color:           theme.text,
  };

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ backgroundColor: theme.background }}>

      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 -top-32 -left-32"
          style={{ background: 'radial-gradient(circle, #00BCD4, transparent)' }} />
        <div className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-15 -bottom-20 -right-20"
          style={{ background: 'radial-gradient(circle, #4CAF50, transparent)' }} />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-10">

        {/* Card */}
        <div
          className="w-full max-w-[400px] rounded-3xl p-8 transition-all duration-700"
          style={{
            backgroundColor: theme.card,
            border:          `1px solid ${theme.border}`,
            boxShadow:       theme.isDark ? '0 24px 80px rgba(0,0,0,0.5)' : '0 24px 80px rgba(0,0,0,0.1)',
            transform:       loaded ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
            opacity:         loaded ? 1 : 0,
          }}
        >
          {/* Logo row */}
          <div className="flex items-center justify-between mb-8">
            <img src="/logo.png" alt="TripData" className="h-7 object-contain" />
            <ThemeToggle />
          </div>

          <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text }}>Sign in</h1>
          <p className="text-sm mb-7" style={{ color: theme.textMuted }}>
            Access your TripData account
          </p>

          {error && (
            <div
              className="mb-5 px-4 py-3 rounded-2xl text-sm"
              style={{
                backgroundColor: theme.isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2',
                color:           '#f87171',
                border:          '1px solid rgba(248,113,113,0.25)',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-2"
                style={{ color: theme.textMuted }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-2xl text-sm outline-none border"
                style={inputStyle}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: theme.textMuted }}>
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs font-semibold"
                  style={{ color: '#00ACC1' }}>
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-11 rounded-2xl text-sm outline-none border"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity"
                  style={{ color: theme.textMuted }}
                >
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-2xl text-white font-bold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
              style={{
                background:  'linear-gradient(135deg, #00BCD4, #4CAF50)',
                boxShadow:   '0 8px 32px rgba(0,172,193,0.35)',
              }}
            >
              {loading ? 'Signing in...' : <><span>Sign in</span><ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="flex items-center justify-center gap-2 mt-6">
            <Shield size={12} style={{ color: theme.textMuted }} />
            <p className="text-xs" style={{ color: theme.textMuted }}>Create Account on App</p>
          </div>
        </div>

        {/* Back to site */}
        <a
          href="https://tripdata.co.za"
          className="flex items-center gap-1.5 mt-5 text-xs font-medium transition-opacity hover:opacity-70"
          style={{ color: theme.textMuted }}
        >
          <ArrowRight size={12} className="rotate-180" />
          Back to tripdata.co.za
        </a>
      </div>
    </div>
  );
}